import * as React from 'react';

/**
 * Badge variant options
 * - default: Neutral styling for general-purpose badges
 * - success: Green styling for positive states (active, approved, complete)
 * - warning: Amber styling for caution states (pending, review required)
 * - error: Red styling for negative states (rejected, failed, expired)
 * - info: Blue styling for informational states (new, updated)
 * - primary: Navy styling for branded/highlighted states
 */
type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';

/**
 * Badge size options
 * - sm: 18px height for compact displays
 * - md: 22px height for standard displays
 */
type BadgeSize = 'sm' | 'md';

/**
 * Base props for the Badge component
 */
interface BadgeBaseProps {
  /**
   * The visual variant of the badge
   * @default 'default'
   */
  variant?: BadgeVariant;
  /**
   * The size of the badge
   * @default 'md'
   */
  size?: BadgeSize;
  /**
   * Whether to show a status dot indicator
   * @default false
   */
  dot?: boolean;
  /**
   * Whether to use pill styling (full border-radius)
   * @default false
   */
  pill?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * The content to display
   */
  children: React.ReactNode;
}

/**
 * BadgeProps extends HTML span attributes
 */
export type BadgeProps = BadgeBaseProps &
  Omit<React.HTMLAttributes<HTMLSpanElement>, keyof BadgeBaseProps>;

/**
 * Variant styles based on the design system (using shared tokens)
 * Each variant includes background, text color, and dot color
 */
const variantClasses: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  default: {
    bg: 'bg-secondary-100',
    text: 'text-secondary-700',
    dot: 'bg-secondary-400',
  },
  success: {
    bg: 'bg-success-50',
    text: 'text-success-800',
    dot: 'bg-success-700',
  },
  warning: {
    bg: 'bg-warning-50',
    text: 'text-warning-800',
    dot: 'bg-warning-700',
  },
  error: {
    bg: 'bg-error-50',
    text: 'text-error-800',
    dot: 'bg-error-700',
  },
  info: {
    bg: 'bg-info-50',
    text: 'text-info-800',
    dot: 'bg-info-700',
  },
  primary: {
    bg: 'bg-primary-100',
    text: 'text-primary-800',
    dot: 'bg-primary-600',
  },
};

/**
 * Size classes based on the design system
 * - sm: 18px height
 * - md: 22px height (default)
 */
const sizeClasses: Record<BadgeSize, { container: string; dot: string }> = {
  sm: {
    container: 'h-[18px] px-2 text-[11px]',
    dot: 'w-[5px] h-[5px]',
  },
  md: {
    container: 'h-[22px] px-2.5 text-[12px]',
    dot: 'w-[6px] h-[6px]',
  },
};

/**
 * Badge component for displaying status indicators, labels, and tags.
 *
 * Designed for insurance industry applications where status communication
 * is critical - policy states, claim statuses, risk levels, etc.
 *
 * @example
 * // Basic usage
 * <Badge>Active</Badge>
 *
 * @example
 * // Success variant with dot
 * <Badge variant="success" dot>Active</Badge>
 *
 * @example
 * // Pill style
 * <Badge variant="warning" pill>Pending Review</Badge>
 *
 * @example
 * // Small size
 * <Badge size="sm" variant="info">New</Badge>
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'md',
      dot = false,
      pill = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const variantStyle = variantClasses[variant];
    const sizeStyle = sizeClasses[size];

    // Combine all classes
    const combinedClasses = [
      // Base styles
      'inline-flex items-center justify-center',
      'font-medium uppercase tracking-[0.05em]',
      // Size
      sizeStyle.container,
      // Border radius
      pill ? 'rounded-full' : 'rounded',
      // Variant colors
      variantStyle.bg,
      variantStyle.text,
      // Custom classes
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <span ref={ref} className={combinedClasses} {...props}>
        {dot && (
          <span
            className={`${sizeStyle.dot} ${variantStyle.dot} rounded-full mr-1.5 flex-shrink-0`}
            aria-hidden="true"
          />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
export type { BadgeVariant, BadgeSize };
